import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { Component, computed, effect, ElementRef, forwardRef, input, OnInit, signal, TemplateRef, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { NestedOption } from '../nested-options';
import { MatTooltip } from '@angular/material/tooltip';


export interface AdvancedSelectValue {
  selectedValues: string[];
  matchAll: boolean;
}

export function createAdvancedSelectValue(): AdvancedSelectValue {
  return {
    selectedValues: [],
    matchAll: false
  };
}

@Component({
  selector: 'app-advanced-select',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatCheckboxModule,
    MatButtonModule,
    MatTooltip
],
  templateUrl: './advanced-select.component.html',
  styleUrl: './advanced-select.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AdvancedSelectComponent),
      multi: true
    }
  ],
  encapsulation: ViewEncapsulation.None // this is necessary to handle local styles properly
})
export class AdvancedSelectComponent implements OnInit, ControlValueAccessor {
  readonly options = input.required<NestedOption[]>();

  readonly label = input('');
  readonly hint = input('');
  readonly placeholder = input('');
  readonly showAllOrAny = input(false);
  readonly showSelectClearAll = input(true);
  readonly allowMultipleSelection = input(true);
  // readonly disabled = input(false);
  readonly resetTrigger = input(0);

  disabled = signal(false);

  readonly flattenedOptions = computed(() => { // Signal with flattened list of all options and categories
    return this.flattenOptions(this.options())
  });
  readonly optionsMap = computed(() => { // map containing option value and option label
    let map = new Map<string, string>();
    for (const item of this.options()) {
      map.set(item.value, item.label)
      if (item.children && item.children.length > 0) {
        for (const child of item.children) {
          map.set(child.value, child.label)
        }
      }
    }
    return map;
  })

  readonly optionFilter = signal(''); // value of input control
  readonly filteredOptions = computed(() => {
    const filter = (this.optionFilter() || '').toLowerCase();
    const options = this.options();
    if (!filter) return options;
    // following meeting of 20251126, it is decided that when a filter string is applied,
    // it should apply to top level elements only (top-level items + categories), not to
    // nested items. However, categories should be open
    // let v = this.recursivelyFilterOptions(options, filter);
    // console.log('v', v)
    let v = this.filterOptions(options, filter);
    return v;
  });
  readonly panelOpen = signal<boolean>(false); // flag controlling the list panel open
  readonly selectedOptions = signal<Set<string>>(new Set()); // list of selected options
  readonly expandedGroups = signal<Set<string>>(new Set()); // list of expanded groups
  readonly matchAll = signal(false);

  // function that checks if a group is expanded or not
  // this is a function that returns a signal, so has to be called with double ()
  isExpanded = (groupId: string) =>
    computed(() => {
      return this.expandedGroups().has(groupId);
    });

  // function that checks if at least one option in the group is selected
  // this is a function that returns a signal, so has to be called with double ()
  isGroupSomeSelected = (group: NestedOption) =>
    computed(() => {
      // If none is selected, select all, else Select none
      let v = (group.children ?? []).some(child => this.selectedOptions().has(child.value));
      return v
    });

  // computes the value to be displayed in the input
  readonly displayValue = computed(() => {
    // console.debug('displayValue', this.panelOpen(), this.selectedOptions().size)
    if (this.selectedOptions().size == 0) {
      return ''
    } else {
      return [...this.selectedOptions()].map(key => this.optionsMap().get(key) ?? key).join(', ')
    }
  })

  @ViewChild('panelTemplate') panelTemplate!: TemplateRef<any>;
  @ViewChild('componentWrapper') componentWrapper!: ElementRef<HTMLDivElement>;
  overlayRef!: OverlayRef;

  constructor(private readonly overlay: Overlay, private readonly vcr: ViewContainerRef) {
    // this automatically expand groups that match the filter AND have children

    effect(() => {
      // console.debug('AdvancedSelectComponent effect', this.label(), this.options().length)
      const filtered = this.filteredOptions();
      const filterString = this.optionFilter();
      const expanded = new Set<string>();
      filtered.forEach(o => {
        if (filterString && o.isGroup && o.children!.length) {
          expanded.add(o.label);
        }
      });
      this.expandedGroups.set(expanded);
    });
  }

  //CVA Start
  private onChange: (value: AdvancedSelectValue) => void = () => { };
  private onTouched: () => void = () => { };

  writeValue(value: AdvancedSelectValue | null): void {
    if (!value) {
      this.selectedOptions.set(new Set());
      this.matchAll.set(false);
      return;
    }
    this.selectedOptions.set(new Set(value.selectedValues));
    this.matchAll.set(value.matchAll);
  }

  registerOnChange(fn: any): void { this.onChange = fn; }
  registerOnTouched(fn: any): void { this.onTouched = fn; }
  setDisabledState(isDisabled: boolean): void {
    // console.log('setDisabledState', isDisabled);
    this.disabled.set(isDisabled);
  }

  //CVA END

  ngOnInit(): void {

  }

  openPanel() {
    // console.log('openPanel', this.options());
    this.panelOpen.set(true);
    if (this.overlayRef) {
      this.overlayRef.detach();
    }

    const positionStrategy = this.overlay.position()
      .flexibleConnectedTo(this.componentWrapper.nativeElement)
      .withPositions([{
        originX: 'start',
        originY: 'bottom',
        overlayX: 'start',
        overlayY: 'top'
      }])
      .withFlexibleDimensions(false)
      .withDefaultOffsetY(-18);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      width: this.componentWrapper.nativeElement.offsetWidth,
      scrollStrategy: this.overlay.scrollStrategies.close(),
      // hasBackdrop: true,              // 👈 adds a transparent backdrop to detect outside clicks
    });

    this.overlayRef.outsidePointerEvents().subscribe(event => {
      const target = event.target as HTMLElement;
      if (
        !this.componentWrapper.nativeElement.contains(target) &&
        !this.panelTemplate.elementRef.nativeElement.contains(target)
      ) {
        this.optionFilter.set('');
        this.closePanel();
      }
    });
    this.overlayRef.attach(new TemplatePortal(this.panelTemplate, this.vcr));
  }

  closePanel() {
    this.panelOpen.set(false);
    this.overlayRef?.detach();
  }

  // creates a flat list of options, including group and children options
  private flattenOptions(options: NestedOption[]): NestedOption[] {
    if (options)
      return options.flatMap(o =>
        o.children ? [o, ...this.flattenOptions(o.children)] : [o]
      );
    else
      return [];
  }

  private simpleFilterOptions(options: NestedOption[], query: string): NestedOption[] {
    // filter top level options only
    return options.filter(o => o.label.toLowerCase().includes(query.toLowerCase()));
  }

  private filterOptions(options: NestedOption[], query: string): NestedOption[] {
    // For top-level items without children: filter out those that do not match the filter
    // For top-level items with children: 
    // -> if top-level item match, do not filter children and expand
    // -> if top-level item doesn't match, filter out children that do not match

    return options
      .map(o => {
        // const matchSelf = o.label.toLowerCase().includes(query);
        const matchSelf = o.label.toLowerCase().includes(query) || (o.alternateLabel && o.alternateLabel.toLowerCase().includes(query));
        let filteredChildren: NestedOption[];
        if (o.isGroup) {
          if (matchSelf) {
            filteredChildren = o.children!;
          } else {
            filteredChildren = this.filterOptions(o.children!, query);
          }
        } else {
          filteredChildren = []
        }
        // console.log('filterOptions', o, matchSelf, filteredChildren.length)
        if (matchSelf || filteredChildren.length) {
          return { ...o, children: filteredChildren };
        }
        return null;
      })
      .filter(Boolean) as NestedOption[];
  }

  // recursively filter matching options
  private recursivelyFilterOptions(options: NestedOption[], query: string): NestedOption[] {
    return options
      .map(o => {
        const matchSelf = o.label.toLowerCase().includes(query);
        const filteredChildren = o.children ? this.recursivelyFilterOptions(o.children, query) : [];
        if (matchSelf || filteredChildren.length) {
          return { ...o, children: filteredChildren };
        }
        return null;
      })
      .filter(Boolean) as NestedOption[];
  }

  private emitValue() {
    const value: AdvancedSelectValue = {
      selectedValues: [...this.selectedOptions()],
      matchAll: this.showAllOrAny() ? this.matchAll() : false
    };
    this.onChange(structuredClone(value)); // clone to avoid reference mutation
  }


  // Select/deselect a single option
  toggleOption(value: string) {
    const values = new Set(this.selectedOptions());
    if (values.has(value)) {
      values.delete(value);
    } else {
      values.add(value);
    }
    this.selectedOptions.set(values);

    this.emitValue();
  }

  // Expand or collapse option group
  toggleGroup(value: NestedOption) {
    const values = new Set(this.expandedGroups());
    if (values.has(value.label)) {
      values.delete(value.label);
    } else {
      values.add(value.label);
    }
    this.expandedGroups.set(values);
  }

  toggleAllOrAny() {
    this.matchAll.set(!this.matchAll());
    this.emitValue();
  }

  // Select/deselect all options (incl. nested ones)
  selectOrClearAllFilter() {
    // first check that options is valid
    const opts = this.options();
    if (!opts || opts.length === 0) {
      this.selectedOptions.set(new Set());
      return;
    }
    // If none is selected, select all, else Select none
    if (this.selectedOptions().size === 0) {
      const current = new Set(this.selectedOptions()); // clone current set
      for (const o of this.flattenedOptions().filter(o => !o.isGroup)) {
        current.add(o.value) // add all new values
      }
      this.selectedOptions.set(current);               
    } else {
      this.selectedOptions.set(new Set());
    }
    this.emitValue();
  }

  // Select/deselect all options inside a group
  selectOrClearAllGroup(group: NestedOption) {
    // check that group has children
    if (!group.children || group.children.length == 0) {
      return
    }
    // If none is selected, select all children, else Select none
    const current = new Set(this.selectedOptions()); // clone current set
    if (this.isGroupSomeSelected(group)()) {
      for (const o of group.children) {
        current.delete(o.value)
      }
    } else {
      for (const o of group.children) {
        current.add(o.value)
      }
    }
    this.selectedOptions.set(current);
    this.emitValue();
  }

  onFilterChange(event: Event) {
    const target = event.target as HTMLInputElement;
    this.optionFilter.set(target.value)
  }

  onFilterFocus() {
    this.openPanel();
  }
}
